import { Directive, inject, OnInit, signal } from '@angular/core';
import { AlertService } from '@context/frontend/alert';
import { DestroyService, DialogService } from '@context/frontend/common';
import { PlaceholderConfig } from '@context/frontend/table';
import { Entity } from '@context/shared/types/common';
import {
	PageSize,
	PaginatedPageDetails,
	SortConfig,
	SortItem,
	SortOptions,
} from '@context/shared/types/pagination';
import { TranslocoService } from '@jsverse/transloco';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { SortDialogComponent } from '../../dialogs';
import {
	Fetchable,
	PaginatedObject,
	PaginatedObjectType,
	PaginatedPageItem,
} from '../../models';

@Directive()
export abstract class PaginationPageBase<
	ContentType extends Entity,
> implements OnInit {
	readonly loading = signal(true);
	readonly sortItems = signal<SortItem<ContentType>[]>([]);
	readonly activeSort = signal<SortItem<ContentType>>(this.sortItems()[0]);

	sortOpen = false;
	filterOpen = false;

	paginated!: PaginatedObject<ContentType>;

	readonly dialog = inject(DialogService);
	readonly alerts = inject(AlertService);
	readonly destroy$ = inject(DestroyService);
	readonly transloco = inject(TranslocoService);

	/**
	 * Signal to track empty state and prevent change detection errors when paginated.totalElements updates asynchronously
	 */
	readonly isEmpty = signal(true);
	readonly tableIndex = signal(0);
	readonly tableLength = signal(0);
	readonly tableDetails = signal<PaginatedPageDetails | null>(null);
	readonly tableSize = signal<PageSize>(10);
	readonly tableItems = signal<PaginatedPageItem<ContentType>[]>([]);

	private defaultOptions: Partial<PaginatedObjectType<ContentType>> = {
		sortBy: 'createdAt',
		sortDir: 'desc',
		constraints: [],
	};

	readonly sortDialog = () =>
		this.dialog.open<SortItem<ContentType> | null>(
			new PolymorpheusComponent(SortDialogComponent),
			SortDialogComponent.Options({
				data: {
					items: this.sortItems(),
					activeItem: this.activeSort(),
				},
			}),
		);

	// These dependencies are supplied by subclasses through `super(...)`, so they
	// are true constructor parameters rather than DI tokens and cannot use inject().
	/* eslint-disable @angular-eslint/prefer-inject */
	constructor(
		protected readonly sortConfig: SortConfig<ContentType>,
		protected readonly placeholder: PlaceholderConfig,
		protected readonly crudService?: Fetchable<ContentType>,
	) {}
	/* eslint-enable @angular-eslint/prefer-inject */

	ngOnInit() {
		this.resetSort();
		this.onLoadItems();
	}

	setOptions(options: Partial<PaginatedObjectType<ContentType>>) {
		Object.assign(this.defaultOptions, options);
	}

	protected async onLoadItems(
		options?: Partial<PaginatedObjectType<ContentType>>,
		reset?: boolean,
	) {
		if (!this.crudService)
			throw new Error(
				'Crud service has not been provided, a custom override for `onLoadItems` may be desired.',
			);

		options = { ...this.defaultOptions, ...options };

		const activeSort = this.activeSort();
		if (this.sortConfig.enabled && activeSort) {
			Object.assign(options, {
				sortBy: activeSort.key,
				sortDir: activeSort.sortDir,
			});
		}

		if (!this.paginated || reset) {
			this.paginated = new PaginatedObject(options);
		} else if (activeSort) {
			this.paginated.update({
				sortBy: activeSort.key,
				sortDir: activeSort.sortDir,
			});
		}

		this.loading.set(true);
		try {
			const value = await this.crudService.fetch(this.paginated);
			this.paginated.update(value);
			await this.onItemsLoaded();
		} catch (error) {
			await this.onItemsLoadedError();
			console.error('There was an issue loading items', error);
			this.alerts.open('loading-data').subscribe();
		} finally {
			this.loading.set(false);
		}
	}

	/**
	 * helps with a more reliable rendering of the table
	 */
	private _onItemsLoaded() {
		this.isEmpty.set(!this.paginated?.totalElements);
		this.tableIndex.set(this.paginated.currentPage);
		this.tableLength.set(this.paginated.pageCount);
		this.tableDetails.set(this.paginated.pageDetails);
		this.tableSize.set(this.paginated.pageSize);
		this.tableItems.set([...this.paginated.items]);
	}

	/**
	 * helps with a more reliable rendering of the table
	 */
	private _onItemsLoadedError() {
		this.isEmpty.set(true);
		this.tableIndex.set(0);
		this.tableLength.set(0);
		this.tableDetails.set(null);
		this.tableSize.set(10);
		this.tableItems.set([]);
	}

	/**
	 * optional override in implementation
	 */
	protected async onItemsLoaded(): Promise<void> {
		this._onItemsLoaded();
	}

	/**
	 * optional override in implementation
	 */
	protected async onItemsLoadedError(): Promise<void> {
		this._onItemsLoadedError();
	}

	protected onPageChange(index: number) {
		this.tableIndex.set(index);
		this.paginated.currentPage = index;
		this.onLoadItems();
	}

	protected onSizeChange(pageSize: PageSize) {
		this.tableSize.set(pageSize);
		this.onLoadItems({ pageSize }, true);
	}

	protected onSortOpen = () =>
		this.sortDialog().subscribe({
			next: (data) => this.onSortSelect(data),
		});

	protected onSortSelect(item: SortItem<ContentType> | null) {
		if (item) this.activeSort.set(item);
		this.sortOpen = false;
		this.onLoadItems({}, true);
	}

	protected resetSort() {
		if (!this.sortConfig.enabled) return;

		const options = SortOptions<ContentType>(this.sortConfig.key);
		this.sortItems.set(options);
		this.activeSort.set(options[0]);
	}
}
